import { Component, Input } from '@angular/core';
import { Location, LocationConsumptionData, LocationPDRDisplay, LocationPODDisplay } from '../../../../core/interfaces/location';
import { CommonModule, DecimalPipe } from '@angular/common';
import { CustomButtonComponent } from "../../../../shared/components/custom-button/custom-button.component";
import { InteractiveMapComponent } from "../interactive-map/interactive-map.component";
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { EmptyPodDataComponent } from "../dummy/empty-pod-data/empty-pod-data.component";
import { EmptyPdrDataComponent } from "../dummy/empty-pdr-data/empty-pdr-data.component";
import { ElectricityInvoice, GasInvoice } from '../../../../core/interfaces/invoice-interfaces';
import { DateConversionService } from '../../../../core/services/date-conversion.service';

@Component({
  selector: 'app-location-info',
  standalone: true,
  imports: [CommonModule, CustomButtonComponent, InteractiveMapComponent, TranslocoPipe, EmptyPodDataComponent, EmptyPdrDataComponent],
  providers: [DecimalPipe],
  templateUrl: './location-info.component.html',
  styleUrl: './location-info.component.css'
})

export class LocationInfoComponent {
  notAvailable: string = "...";

  constructor(private translocoService: TranslocoService, private decimalPipe: DecimalPipe, private dateConversionService: DateConversionService) {
    this.translocoService.selectTranslate('shared.not_available')
      .subscribe(translation => {
        this.notAvailable = translation;
      });
  }

  @Input() displayNumber: number | null = null;
  @Input() location: Location | null = null;
  @Input() locationConsumption: LocationConsumptionData | null = null;
  @Input() locationElectricityData: ElectricityInvoice | null = null;
  @Input() locationGasData: GasInvoice | null = null;

  // Callback function for editing
  @Input() editLocationCallbackFunction!: (location: Location) => void;

  getVatValue(): string {
    var vat = this.notAvailable;

    const isNumeric = (val: any) => val !== null && val !== '' && !isNaN(Number(val));

    const electricityVat = this.locationElectricityData?.expected_vat_rate;
    const gasVat = this.locationGasData?.expected_vat_rate;

    if (this.locationElectricityData != null && electricityVat != null) {
      if (isNumeric(electricityVat)) {
        vat = this.decimalPipe.transform(electricityVat, '1.2-2') || this.notAvailable;
      }
      else {
        vat = electricityVat.toString();
      }
    }
    else if (this.locationGasData != null && gasVat != null) {
      if (isNumeric(gasVat)) {
        vat = this.decimalPipe.transform(gasVat, '1.2-2') || this.notAvailable;
      }
      else {
        vat = gasVat.toString();
      }
    }
    return vat;
  }

  get selectedPods(): LocationPODDisplay[] {
    var locationDisplay: LocationPODDisplay[] = [];

    if (this.locationElectricityData != null) {
      for (var pod of this.locationElectricityData.pods) {
        const newLocationDisplay: LocationPODDisplay = {
          location_id: this.location?.id ?? '',

          supplier: pod.supplier?.name || this.notAvailable,

          excise_taxes: pod?.excise_taxes != null
            ? this.decimalPipe.transform(pod.excise_taxes, '1.2-2') ?? this.notAvailable
            : this.notAvailable,

          pod_id: pod?.pod_id || this.notAvailable,

          vat: this.getVatValue(),

          available_capacity: pod?.available_capacity != null
            ? this.decimalPipe.transform(pod.available_capacity, '1.2-2') ?? this.notAvailable
            : this.notAvailable,

          tension: pod?.network_tension ?? this.notAvailable,

          start_of_supply: pod.supplier?.start_of_supply
            ? this.dateConversionService.isoToFormattedDate(pod.supplier.start_of_supply) ?? this.notAvailable
            : this.notAvailable,

          end_of_supply: pod.supplier?.end_of_supply
            ? this.dateConversionService.isoToFormattedDate(pod.supplier.end_of_supply) ?? this.notAvailable
            : this.notAvailable,
        };
        locationDisplay = [...locationDisplay, newLocationDisplay];
      }
    }

    return locationDisplay;
  }

  get selectedPdrs(): LocationPDRDisplay[] {
    var locationDisplay: LocationPDRDisplay[] = [];

    if (this.locationGasData != null) {
      for (var pod of this.locationGasData.pdrs) {
        const newLocationDisplay: LocationPDRDisplay = {
          location_id: this.location?.id ?? '',

          supplier: pod.supplier?.name || this.notAvailable,

          pdr_id: pod?.pdr_id || this.notAvailable,

          vat: this.getVatValue(),

          remi: pod?.REMI_type_gas || this.notAvailable,

          start_of_supply: pod.supplier?.start_of_supply
            ? this.dateConversionService.isoToFormattedDate(pod.supplier.start_of_supply) ?? this.notAvailable
            : this.notAvailable,

          end_of_supply: pod.supplier?.end_of_supply
            ? this.dateConversionService.isoToFormattedDate(pod.supplier.end_of_supply) ?? this.notAvailable
            : this.notAvailable,
        };
        locationDisplay = [...locationDisplay, newLocationDisplay];
      }
    }

    return locationDisplay;
  }

  editLocation() {
    if (this.location) {
      this.editLocationCallbackFunction(this.location!);
    }
  }
}
